import axios from 'axios';

// Get the current origin (protocol + hostname + port) from the browser
const currentOrigin = window.location.origin;
console.log('Current origin:', currentOrigin);
console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

// For production, use env var (ALB URL). For development, use env var or localhost
const API_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || currentOrigin)  // Use ALB URL in production
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000');

console.log('Final API URL:', API_URL); // For debugging

const api = axios.create({
  baseURL: `${API_URL}/api`,  // Add /api prefix to all routes
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  withCredentials: false // Disable credentials for simpler CORS
});

// Add token and organization context to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // For super_admin org switching: send selected org as header
  const selectedOrgId = localStorage.getItem('selectedOrganizationId');
  if (selectedOrgId) {
    config.headers['X-Organization-Id'] = selectedOrgId;
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip interceptor for login requests
    if (originalRequest._skipAuthInterceptor) {
      return Promise.reject(error);
    }

    // If 403 with consent_required, redirect to consent page
    if (error.response?.status === 403 && error.response?.data?.status === 'consent_required') {
      if (window.location.pathname !== '/consent') {
        window.location.href = '/consent';
      }
      // Return a never-resolving promise to prevent calling code from crashing
      // while the page redirect is in progress
      return new Promise(() => {});
    }

    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await api.post('/auth/refresh-token', { refreshToken });
        const { token } = response.data.data;

        // Update token in localStorage
        localStorage.setItem('token', token);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        // If refresh fails, logout user
        auth.logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to trigger storage event for cross-tab communication
const triggerStorageEvent = (key, value) => {
  // Create and dispatch a storage event
  const event = new StorageEvent('storage', {
    key: key,
    newValue: value,
    url: window.location.href
  });
  window.dispatchEvent(event);
};

// Auth service
export const auth = {
  login: async (email, password) => {
    try {
      // Mark this request to skip the auth interceptor
      const response = await api.post('/auth/login', { email, password }, {
        _skipAuthInterceptor: true
      });

      if (response.data.status === 'success') {
        const { token, user } = response.data.data;

        // Store token and user data
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        // Store organizationId from user context
        if (user.organizationId) {
          localStorage.setItem('organizationId', user.organizationId);
        }

        // Trigger storage event for cross-tab communication
        triggerStorageEvent('user', JSON.stringify(user));

        return response.data.data;
      }

      throw new Error(response.data.message);
    } catch (error) {
      // Re-throw the full error object so Login.js can access error.response.data
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('organizationId');
    localStorage.removeItem('selectedOrganizationId');
    // Trigger storage event for cross-tab communication
    triggerStorageEvent('user', null);
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  }
};

// Patients service
export const patients = {
  getAll: () => api.get('/patients'),
  getById: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.put(`/patients/${id}`, data, {
        headers: { 'Content-Type': undefined }
      });
    }
    return api.put(`/patients/${id}`, data);
  },
  updateStatus: (id, status) => api.patch(`/patients/${id}/status`, { status }),
  getLaudoText: (id) => api.get(`/patients/${id}/laudo-text`).then(res => res.data),
  processLaudo: (id) => api.post(`/patients/${id}/process-laudo`).then(res => res.data),
  analyzeLaudo: (id) => api.post(`/patients/${id}/analyze-laudo`).then(res => res.data),
  scoreLaudo: (id) => api.post(`/patients/${id}/score-laudo`).then(res => res.data),
  exportScoreReport: async (id, extraHeaders = {}) => {
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      ...extraHeaders
    };
    const selectedOrgId = localStorage.getItem('selectedOrganizationId');
    if (selectedOrgId) {
      headers['X-Organization-Id'] = selectedOrgId;
    }
    const response = await fetch(`${API_URL}/api/patients/${id}/export-laudo-completo`, {
      headers
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `Laudo_Completo_${Date.now()}.docx`; // Default fallback

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    const isEncrypted = response.headers.get('X-Encrypted') === 'true';

    return { data: blob, filename, isEncrypted };
  },
};

// Staff service
export const staff = {
  getAllTherapists: () => api.get('/users/by-role/therapist'),
  getAllDoctors: () => api.get('/users/by-role/doctor')
};

// Insurance service
export const insurance = {
  getAll: () => api.get('/insurance-companies'),
  getById: (id) => api.get(`/insurance-companies/${id}`)
};

// Sessions service
export const sessions = {
  getAll: () => api.get('/sessions'),
  getById: (id) => api.get(`/sessions/${id}`),
  create: (data) => api.post('/sessions', data),
  update: (id, data) => api.put(`/sessions/${id}`, data)
};

// Treatment Plans service
export const treatmentPlans = {
  getAll: () => api.get('/treatment-plans'),
  getById: (id) => api.get(`/treatment-plans/${id}`),
  create: (data) => api.post('/treatment-plans', data),
  update: (id, data) => api.put(`/treatment-plans/${id}`, data),
  generate: (data) => api.post('/treatment-plans/generate', data),
  getByAssessment: (assessmentId) => api.get(`/treatment-plans/assessment/${assessmentId}`),
  getLatestByAssessment: (assessmentId) => api.get(`/treatment-plans/assessment/${assessmentId}/latest`)
};

// Session Notes service
export const sessionNotes = {
  getAll: () => api.get('/session-notes'),
  getById: (id) => api.get(`/session-notes/${id}`),
  create: (data) => api.post('/session-notes', data),
  update: (id, data) => api.put(`/session-notes/${id}`, data)
};

// Behaviors service
export const behaviors = {
  // Get all behaviors from catalog
  getCatalog: () => api.get('/behaviors/catalog'),
  
  // Get behavior records for a patient
  getByPatient: (patientId, params) => api.get(`/behaviors/patient/${patientId}`, { params }),
  
  // Create behavior record
  createRecord: (data) => api.post('/behaviors/records', data),
  
  // Update behavior record
  updateRecord: (id, data) => api.put(`/behaviors/records/${id}`, data),
  
  // Delete behavior record
  deleteRecord: (id) => api.delete(`/behaviors/records/${id}`),
  
  // Get behavior record by ID
  getRecordById: (id) => api.get(`/behaviors/records/${id}`)
};

// Parent Management service
export const parentManagement = {
  // Get all parent users
  getAllParents: () => api.get('/users/by-role/parent'),
  
  // Create parent user
  createParent: (data) => api.post('/users', { ...data, role: 'parent' }),
  
  // Update parent user
  updateParent: (id, data) => api.put(`/users/${id}`, data),
  
  // Get parent by ID
  getParentById: (id) => api.get(`/users/${id}`),
  
  // Delete parent user
  deleteParent: (id) => api.delete(`/users/${id}`)
};

// Parent-Child Assignment service
export const parentAssignments = {
  // Get all parent-child assignments (admin only)
  getAll: () => api.get('/parent-assignments'),
  
  // Get assignments for a specific parent
  getByParent: (parentId) => api.get(`/parent-assignments/parent/${parentId}/children`),
  
  // Get assignments for a specific patient
  getByPatient: (patientId) => api.get(`/parent-assignments/patient/${patientId}`),
  
  // Get current user's children (for parent users)
  getMyChildren: () => api.get('/parent-assignments/my-children'),
  
  // Create new assignment
  create: (data) => api.post('/parent-assignments', data),
  
  // Update assignment
  update: (id, data) => api.put(`/parent-assignments/${id}`, data),
  
  // Delete assignment
  delete: (id) => api.delete(`/parent-assignments/${id}`),
  
  // Get assignment by ID
  getById: (id) => api.get(`/parent-assignments/${id}`)
};

// Users service
export const users = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  updateAssignments: (id, data) => api.put(`/users/${id}/assignments`, data),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
  resendInvite: (id) => api.post(`/users/${id}/resend-invite`)
};

// User Security service
export const userSecurity = {
  updatePassword: (data) => api.post('/users/update-password', data),
  resetPassword: (email) => api.post('/users/reset-password', { email })
};

// Role Permissions service
export const rolePermissions = {
  getAll: () => api.get('/roles'),
  getById: (id) => api.get(`/roles/${id}`),
  update: (id, data) => api.put(`/roles/${id}`, data)
};

// Assessment service
export const assessments = {
  // Get all assessment protocols
  getProtocols: () => api.get('/assessments/protocols'),
  
  // Get assessments for a patient
  getByPatient: (patientId) => api.get(`/assessments/patients/${patientId}/assessments`),
  
  // Upload assessment file
  upload: (patientId, formData) => api.post(`/assessments/patients/${patientId}/assessments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Analyze assessment (extract text and clean PII)
  analyze: (assessmentId) => api.post(`/assessments/${assessmentId}/analyze`),

  // Get parsed assessment data
  getParsedData: (assessmentId) => api.get(`/assessments/${assessmentId}/parsed-data`),

  // Get single assessment
  getById: (assessmentId) => api.get(`/assessments/${assessmentId}`),

  // Download assessment file (cache-bust to avoid stale 304 responses)
  download: (assessmentId) => api.get(`/assessments/${assessmentId}/download`, {
    responseType: 'blob',
    params: { _t: Date.now() }
  }),

  // Update assessment
  update: (assessmentId, data) => api.put(`/assessments/${assessmentId}`, data),

  // Delete assessment
  delete: (assessmentId) => api.delete(`/assessments/${assessmentId}`),

  // Generate parent-friendly report (Social Skills)
  generateParentReport: (assessmentId) => api.post(`/assessments/${assessmentId}/parent-report`)
};

// Fetch patient observations by patientId and date range
export const getPatientObservations = (patientId, startDate, endDate) => {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  return api.get(`/patients/${patientId}/observations`, { params });
};

export const triggerObservationAnalysis = (patientId) => {
  return api.post(`/patients/${patientId}/observation-analysis`);
};

export const userPatientAssignments = {
  // Get all assignments
  getAllAssignments: () => api.get('/user-patient-assignments'),
  
  // Get assignments for a specific user
  getUserAssignments: (userId) => api.get(`/user-patient-assignments/user/${userId}`),
  
  // Get assignments for a specific patient
  getPatientAssignments: (patientId) => api.get(`/user-patient-assignments/patient/${patientId}`),
  
  // Get eligible users (supervisors and therapists)
  getEligibleUsers: () => api.get('/user-patient-assignments/eligible-users'),
  
  // Get eligible patients
  getEligiblePatients: () => api.get('/user-patient-assignments/eligible-patients'),
  
  // Get patients accessible by a user
  getUserAccessiblePatients: (userId) => api.get(`/user-patient-assignments/user/${userId}/accessible-patients`),
  
  // Create new assignment
  createAssignment: (assignmentData) => api.post('/user-patient-assignments', assignmentData),
  
  // Update assignment
  updateAssignment: (id, assignmentData) => api.put(`/user-patient-assignments/${id}`, assignmentData),
  
  // Delete assignment
  deleteAssignment: (id) => api.delete(`/user-patient-assignments/${id}`)
};

// LGPD / Privacy service
export const lgpd = {
  getConsentStatus: () => api.get('/lgpd/consent-status'),
  acceptConsent: (data) => api.post('/lgpd/accept-consent', data),
  acceptMultipleConsents: (consents) => api.post('/lgpd/accept-consents', { consents }),
  revokeConsent: (consentType) => api.post('/lgpd/revoke-consent', { consentType }),
  getConsentHistory: () => api.get('/lgpd/consent-history'),
  getPrivacyPolicy: (version) => api.get('/lgpd/privacy-policy', { params: { version } }),
  getTermsOfUse: (version) => api.get('/lgpd/terms-of-use', { params: { version } }),
  // Data correction requests
  createCorrectionRequest: (data) => api.post('/lgpd/correction-requests', data),
  listCorrectionRequests: (params) => api.get('/lgpd/correction-requests', { params }),
  getCorrectionRequest: (id) => api.get(`/lgpd/correction-requests/${id}`),
  reviewCorrectionRequest: (id, data) => api.put(`/lgpd/correction-requests/${id}/review`, data),
  completeCorrectionRequest: (id) => api.put(`/lgpd/correction-requests/${id}/complete`),
  // Anonymization
  anonymizeExport: () => api.post('/lgpd/anonymize-export'),
};

export const auditLogs = {
  getAll: (params) => api.get('/audit-logs', { params }),
  getStats: (params) => api.get('/audit-logs/stats', { params }),
  exportCSV: (params, extraHeaders = {}) => api.get('/audit-logs/export', { params: { ...params, format: 'csv' }, responseType: 'blob', headers: extraHeaders }),
  exportXML: (params, extraHeaders = {}) => api.get('/audit-logs/export', { params: { ...params, format: 'xml' }, responseType: 'blob', headers: extraHeaders }),
};

// Organizations service (super_admin only)
export const emailLogs = {
  getAll: (params) => api.get('/email-logs', { params }),
  getStats: (params) => api.get('/email-logs/stats', { params }),
};

export const organizations = {
  getAll: () => api.get('/organizations'),
  getById: (id) => api.get(`/organizations/${id}`),
  create: (data) => api.post('/organizations', data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  delete: (id) => api.delete(`/organizations/${id}`),
  // Switch active organization (stores in localStorage, sent via interceptor)
  switchOrg: (orgId) => {
    if (orgId) {
      localStorage.setItem('selectedOrganizationId', orgId);
    } else {
      localStorage.removeItem('selectedOrganizationId');
    }
  },
  getSelectedOrgId: () => localStorage.getItem('selectedOrganizationId')
};

// Audit Reports service
export const auditReports = {
  getByPatient: (patientId) => api.get(`/audit-reports/patients/${patientId}/audit-reports`),
  getById: (reportId) => api.get(`/audit-reports/${reportId}`),
  download: (reportId) => api.get(`/audit-reports/${reportId}/download`, { responseType: 'blob' }),
  delete: (reportId) => api.delete(`/audit-reports/${reportId}`),
  validate: (reportId) => api.post(`/audit-reports/${reportId}/validate`),
  reopen: (reportId) => api.post(`/audit-reports/${reportId}/reopen`),

  // Collaboration: discussion threads, editing by the responsável, history
  listComments: (reportId) => api.get(`/audit-reports/${reportId}/comments`),
  addComment: (reportId, data) => api.post(`/audit-reports/${reportId}/comments`, data),
  updateComment: (commentId, body) => api.patch(`/audit-reports/comments/${commentId}`, { body }),
  deleteComment: (commentId) => api.delete(`/audit-reports/comments/${commentId}`),
  resolveThread: (reportId, data) => api.post(`/audit-reports/${reportId}/threads/resolve`, data),
  updateContent: (reportId, data) => api.put(`/audit-reports/${reportId}/content`, data),
  listRevisions: (reportId) => api.get(`/audit-reports/${reportId}/revisions`),

  // Automated audit ("Gerar Auditoria"): clinic documents and runs
  listDocuments: (patientId) => api.get(`/audit-reports/patients/${patientId}/source-documents`),
  uploadDocuments: (patientId, formData) => api.post(`/audit-reports/patients/${patientId}/source-documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateDocument: (documentId, discipline) => api.patch(`/audit-reports/source-documents/${documentId}`, { discipline }),
  deleteDocument: (documentId) => api.delete(`/audit-reports/source-documents/${documentId}`),
  startRun: (patientId, documentIds) => api.post(`/audit-reports/patients/${patientId}/runs`, { documentIds }),
  getLatestRun: (patientId) => api.get(`/audit-reports/patients/${patientId}/runs/latest`),
  getRun: (runId) => api.get(`/audit-reports/runs/${runId}`)
};

export default api; 